import { useState } from "react";

export interface FilterState {
  region: string;
  year: string;
  sort: string;
}

interface FilterBarProps {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
  resultCount: number;
}

const sortOptions = [
  { key: "latest", label: "按最新" },
  { key: "hot", label: "按最热" },
  { key: "rating", label: "按评分" },
];

function Options({
  label,
  options,
  value,
  onPick,
}: {
  label: string;
  options: string[];
  value: string;
  onPick: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200/60 py-3 last:border-b-0 md:flex-row md:items-center md:gap-4 md:border-b-0 md:py-0">
      <span className="w-16 flex-none text-sm font-medium text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onPick(opt)}
            className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-sm transition ${
              value === opt
                ? "bg-primary-500 text-background-50"
                : "bg-background-200/60 text-foreground-700 hover:bg-secondary-200 hover:text-foreground-950"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({ filters, onChange, onReset, resultCount }: FilterBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const regions = ["全部", "中国大陆", "美国", "韩国", "日本", "英国"];
  const years = ["全部", "2025", "2024", "2023", "2022"];
  const sortLabels = sortOptions.map((s) => s.label);

  const apply = (patch: Partial<FilterState>) => onChange({ ...filters, ...patch });

  return (
    <>
      {/* Desktop */}
      <div className="hidden rounded-lg border border-background-200/70 bg-background-100 px-5 py-4 md:block">
        <Options
          label="地区"
          options={regions}
          value={filters.region}
          onPick={(v) => apply({ region: v })}
        />
        <div className="my-3 h-px bg-background-200/60" />
        <Options
          label="年份"
          options={years}
          value={filters.year}
          onPick={(v) => apply({ year: v })}
        />
        <div className="my-3 h-px bg-background-200/60" />
        <Options
          label="排序"
          options={sortLabels}
          value={sortOptions.find((s) => s.key === filters.sort)?.label || "按最新"}
          onPick={(v) => {
            const found = sortOptions.find((s) => s.label === v);
            apply({ sort: found ? found.key : "latest" });
          }}
        />
      </div>

      {/* Mobile */}
      <div className="md:hidden">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border border-background-200 bg-background-100 py-3 text-sm font-medium text-foreground-900"
          >
            <i className="ri-filter-3-line"></i>
            筛选条件
          </button>
          <button
            type="button"
            onClick={onReset}
            className="cursor-pointer rounded-md border border-background-200 bg-background-100 px-4 py-3 text-sm text-foreground-700"
          >
            重置
          </button>
        </div>
        <p className="mt-2 text-xs text-foreground-500">已筛选出 {resultCount} 部影视资源</p>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl border-t border-background-200 bg-background-50 px-5 pb-6 pt-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <Options
              label="地区"
              options={regions}
              value={filters.region}
              onPick={(v) => apply({ region: v })}
            />
            <Options
              label="年份"
              options={years}
              value={filters.year}
              onPick={(v) => apply({ year: v })}
            />
            <Options
              label="排序"
              options={sortLabels}
              value={sortOptions.find((s) => s.key === filters.sort)?.label || "按最新"}
              onPick={(v) => {
                const found = sortOptions.find((s) => s.label === v);
                apply({ sort: found ? found.key : "latest" });
              }}
            />
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  onReset();
                }}
                className="flex-1 whitespace-nowrap rounded-md border border-background-200 bg-background-100 py-3 text-sm text-foreground-800"
              >
                重置筛选
              </button>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="flex-1 whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}